import React from 'react';
import BlindIcon from './icons/affectedPerson/blind';
import CognitiveIcon from './icons/affectedPerson/cognitive';
import MobilityIcon from './icons/affectedPerson/mobility';
import ColorBlindnessIcon from './icons/affectedPerson/ColorBlindness';
import DeafIcon from './icons/affectedPerson/deaf';
import AttentionDeficitIcon from './icons/affectedPerson/attentionDeficit';
import DeafBlindIcon from './icons/affectedPerson/deafblind';
import LowVisionIcon from './icons/affectedPerson/low-vision';

export type AffectedDisability = 
  | 'blind' 
  | 'lowVision' 
  | 'cognitive' 
  | 'mobility' 
  | 'colorblindness' 
  | 'deaf' 
  | 'attentiondeficit' 
  | 'hearing'
  | 'deafBlind';

interface AffectedDisabilitiesProps {
  disabilities: AffectedDisability[];
  className?: string;
  iconSize?: number;
}

export const AffectedDisabilities: React.FC<AffectedDisabilitiesProps> = ({ 
  disabilities, 
  className = '',
  iconSize = 20
}) => {
  // widthRatio: for icons wider than square (e.g. deafBlind 57x32) so the wrapper gets enough width
  const disabilityIconMap: Record<
    AffectedDisability,
    { icon: React.ComponentType<{ stroke?: string; fill?: string }>; name: string; widthRatio?: number }
  > = {
    blind: {
      icon: BlindIcon,
      name: 'Blind',
    },
    lowVision: {
      icon: LowVisionIcon,
      name: 'Low Vision',
    },
    cognitive: {
      icon: CognitiveIcon,
      name: 'Cognitive',
    },
    mobility: {
      icon: MobilityIcon,
      name: 'Mobility',
    },
    colorblindness: {
      icon: ColorBlindnessIcon,
      name: 'Color Blindness',
    },
    deaf: {
      icon: DeafIcon,
      name: 'Deaf',
    },
    hearing: {
      icon: DeafIcon,
      name: 'Hearing',
    },
    attentiondeficit: {
      icon: AttentionDeficitIcon,
      name: 'Attention Deficit',
    },
    deafBlind: {
      icon: DeafBlindIcon,
      name: 'Deaf Blind',
      widthRatio: 57 / 32, // SVG is 57x32 so needs wider slot than a single square icon
    },
  };

  // Filter out duplicates and invalid disabilities
  const uniqueDisabilities = Array.from(new Set(disabilities)).filter(
    disability => disability && disabilityIconMap[disability]
  );

  if (uniqueDisabilities.length === 0) {
    return null;
  }

  return (
    <div className={`cy:flex cy:flex-row cy:flex-nowrap cy:items-center cy:gap-2 ${className}`}>
      {uniqueDisabilities.map((disability) => {
        const iconData = disabilityIconMap[disability];
        if (!iconData) return null;

        const IconComponent = iconData.icon;
        const widthRatio = iconData.widthRatio ?? 1;
        const wrapperWidth = Math.ceil(iconSize * widthRatio);

        return (
          <div
            key={disability}
            className="cy:relative cy:group cy:flex-shrink-0"
            title={iconData.name}
            style={{
              width: `${wrapperWidth}px`,
              height: `${iconSize}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                width: `${32 * widthRatio}px`,
                height: '32px',
                transform: `scale(${iconSize / 32})`,
                transformOrigin: 'center',
              }}
            >
              <IconComponent />
            </div>
            
            {/* Tooltip */}
            <div className="cy:absolute cy:bottom-full cy:left-1/2 cy:transform cy:-translate-x-1/2 cy:mb-2 cy:px-2 cy:py-1 cy:bg-gray-800 cy:text-white cy:text-xs cy:rounded cy:opacity-0 cy:group-hover:opacity-100 cy:transition-opacity cy:pointer-events-none cy:whitespace-nowrap cy:z-10">
              {iconData.name}
              <div className="cy:absolute cy:top-full cy:left-1/2 cy:transform cy:-translate-x-1/2 cy:border-4 cy:border-transparent cy:border-t-gray-800"></div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

